
import React, { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { Edit, Trash2, Plus, ArrowLeft } from 'lucide-react'
import TechStackForm from './TechStackForm'

interface TechStackItem {
  id: string
  category: string
  technologies: string[]
  image_url: string | null
  display_order: number
  is_active: boolean
}

const TechStackManager = () => {
  const [techStackItems, setTechStackItems] = useState<TechStackItem[]>([])
  const [loading, setLoading] = useState(true)
  const [editingItem, setEditingItem] = useState<TechStackItem | null>(null)
  const [showForm, setShowForm] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    fetchTechStackItems()
  }, [])

  const fetchTechStackItems = async () => {
    try {
      const { data, error } = await supabase
        .from('tech_stack')
        .select('*')
        .order('display_order')
      
      if (error) throw error
      setTechStackItems(data || [])
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch tech stack items",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tech stack item?')) return

    try {
      const { error } = await supabase
        .from('tech_stack')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      
      toast({
        title: "Success",
        description: "Tech stack item deleted successfully",
      })
      
      fetchTechStackItems()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete tech stack item",
        variant: "destructive",
      })
    }
  }

  const handleEdit = (item: TechStackItem) => {
    setEditingItem(item)
    setShowForm(true)
  }

  const handleAdd = () => {
    setEditingItem(null)
    setShowForm(true)
  }

  const handleFormSave = () => {
    setShowForm(false)
    setEditingItem(null)
    fetchTechStackItems()
  }

  const handleFormCancel = () => {
    setShowForm(false)
    setEditingItem(null)
  }

  if (loading) {
    return <div>Loading tech stack items...</div>
  }

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleFormCancel}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tech Stack
          </Button>
          <h2 className="text-2xl font-bold">
            {editingItem ? 'Edit Tech Stack Item' : 'Add New Tech Stack Item'}
          </h2>
        </div>
        <TechStackForm
          techStackItem={editingItem}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Manage Tech Stack</h2>
        <Button onClick={handleAdd}>
          <Plus className="w-4 h-4 mr-2" />
          Add Tech Stack Item
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tech Stack Items</CardTitle>
          <CardDescription>Manage your technology stack categories</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead>Technologies</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {techStackItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.category}</TableCell>
                  <TableCell>{item.technologies.join(', ')}</TableCell>
                  <TableCell>{item.display_order}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEdit(item)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

export default TechStackManager
